/**
 * MFA Challenge Component (DC-905)
 *
 * Displays the MFA challenge during login flow.
 * Features:
 * - 6-digit TOTP input with auto-focus and auto-submit
 * - Backup code option
 * - Rate limit warnings
 */

import React, { useState, useRef, useEffect } from 'react';
import type { AuthUser } from '../../contexts/AuthContext';

interface MFAChallengeProps {
  /** Temporary MFA token from login response */
  mfaToken: string;
  /** Email for display */
  email: string;
  /** Called on successful MFA verification with access token */
  onSuccess: (accessToken: string, user: AuthUser) => void;
  /** Called when user cancels */
  onCancel: () => void;
}

export function MFAChallenge({ mfaToken, email, onSuccess, onCancel }: MFAChallengeProps) {
  const [mode, setMode] = useState<'totp' | 'backup'>('totp');
  const [code, setCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const backupInputRef = useRef<HTMLInputElement | null>(null);

  // Focus first input on mount
  useEffect(() => {
    if (mode === 'totp') {
      inputRefs.current[0]?.focus();
    } else {
      backupInputRef.current?.focus();
    }
  }, [mode]);

  // Handle 6-digit TOTP input
  const handleCodeChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newCode = code.split('');
    newCode[index] = digit;
    const updatedCode = newCode.join('').slice(0, 6);
    setCode(updatedCode);

    // Auto-focus next input
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit on 6 digits
    if (updatedCode.length === 6) {
      handleVerifyTOTP(updatedCode);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    setCode(pastedData);

    if (pastedData.length === 6) {
      handleVerifyTOTP(pastedData);
    } else {
      inputRefs.current[pastedData.length]?.focus();
    }
  };

  const handleVerifyTOTP = async (totpCode: string = code) => {
    if (totpCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/auth/mfa/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-MFA-Token': mfaToken,
        },
        body: JSON.stringify({ token: totpCode }),
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.data) {
        onSuccess(data.data.accessToken, data.data.user);
      } else {
        const errorData = data.error || {};
        setError(errorData.message || 'Invalid verification code');
        setCode('');
        inputRefs.current[0]?.focus();

        if (errorData.details?.attemptsRemaining !== undefined) {
          setAttemptsRemaining(errorData.details.attemptsRemaining);
        }
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyBackup = async () => {
    const cleanCode = backupCode.replace(/\s/g, '').toUpperCase();
    if (cleanCode.length < 8) {
      setError('Please enter a valid backup code');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/auth/mfa/backup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-MFA-Token': mfaToken,
        },
        body: JSON.stringify({ code: cleanCode }),
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.data) {
        onSuccess(data.data.accessToken, data.data.user);
      } else {
        const errorData = data.error || {};
        setError(errorData.message || 'Invalid backup code');
        setBackupCode('');
        backupInputRef.current?.focus();

        if (errorData.details?.attemptsRemaining !== undefined) {
          setAttemptsRemaining(errorData.details.attemptsRemaining);
        }
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mfa-challenge">
      <div className="mfa-challenge-header">
        <div className="mfa-challenge-icon">
          <span>&#128274;</span>
        </div>
        <h2 className="mfa-challenge-title">Two-Factor Authentication</h2>
        <p className="mfa-challenge-subtitle">
          {mode === 'totp'
            ? 'Enter the 6-digit code from your authenticator app'
            : 'Enter one of your backup codes'}
        </p>
        <p className="mfa-challenge-email">{email}</p>
      </div>

      {error && (
        <div className="auth-error" style={{ marginBottom: '1rem' }}>
          <span className="auth-error-icon">!</span>
          {error}
        </div>
      )}

      {attemptsRemaining !== null && attemptsRemaining <= 3 && (
        <div className="mfa-rate-limit-warning">
          <span className="mfa-warning-icon">!</span>
          {attemptsRemaining === 0
            ? 'Too many failed attempts. Please wait before trying again.'
            : `${attemptsRemaining} attempt${attemptsRemaining === 1 ? '' : 's'} remaining`}
        </div>
      )}

      {mode === 'totp' ? (
        <>
          <div className="mfa-code-input-container">
            <div className="mfa-code-inputs" onPaste={handlePaste}>
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <input
                  key={index}
                  ref={(el) => { inputRefs.current[index] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className="mfa-code-input"
                  value={code[index] || ''}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  disabled={isSubmitting || attemptsRemaining === 0}
                  autoFocus={index === 0}
                />
              ))}
            </div>
          </div>

          <div className="mfa-challenge-actions">
            <button
              type="button"
              className="auth-button"
              onClick={() => handleVerifyTOTP()}
              disabled={isSubmitting || code.length !== 6 || attemptsRemaining === 0}
            >
              {isSubmitting ? (
                <>
                  <span className="auth-button-spinner" />
                  Verifying...
                </>
              ) : (
                'Verify'
              )}
            </button>
          </div>

          <div className="mfa-challenge-alt">
            <button
              type="button"
              className="mfa-alt-link"
              onClick={() => {
                setMode('backup');
                setError(null);
                setCode('');
              }}
              disabled={isSubmitting}
            >
              Use a backup code instead
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="mfa-backup-input-container">
            <input
              ref={backupInputRef}
              type="text"
              className="auth-input mfa-backup-input"
              placeholder="XXXX-XXXX"
              value={backupCode}
              onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
              disabled={isSubmitting || attemptsRemaining === 0}
              autoFocus
            />
            <p className="mfa-backup-hint">
              Enter one of your 10-character backup codes
            </p>
          </div>

          <div className="mfa-challenge-actions">
            <button
              type="button"
              className="auth-button"
              onClick={handleVerifyBackup}
              disabled={isSubmitting || backupCode.length < 8 || attemptsRemaining === 0}
            >
              {isSubmitting ? (
                <>
                  <span className="auth-button-spinner" />
                  Verifying...
                </>
              ) : (
                'Use Backup Code'
              )}
            </button>
          </div>

          <div className="mfa-challenge-alt">
            <button
              type="button"
              className="mfa-alt-link"
              onClick={() => {
                setMode('totp');
                setError(null);
                setBackupCode('');
              }}
              disabled={isSubmitting}
            >
              Use authenticator app instead
            </button>
          </div>
        </>
      )}

      <div className="mfa-challenge-footer">
        <button
          type="button"
          className="mfa-cancel-link"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel and sign out
        </button>
      </div>
    </div>
  );
}

export default MFAChallenge;
